import { useCallback } from 'react'

import { Card, Table } from '@circlefin/components'
import { useModal } from '@circlefin/modal-router'
import { usePermission } from '@circlefin/permissions/react'
import { useTravelRuleRequired } from '@features/users/hooks/travel-rule'
import { STRIPE_ENABLED } from '@services/permissions/sets/account/stripeEnabled/stripeEnabled'
import { routes } from '@services/sections/modal/routes'
import { useSegment, SegmentEvents } from '@services/segment'
import { SimpleTable } from '@shared/components/tables'
import {
  LegalEntity,
  useCardPaymentMethodsQuery,
  useEntityQuery,
} from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import * as Buttons from '../../../buttons'

import { LinkedCardsTableRow } from './LinkedCardsTableRow'

export const LinkedCardsTable: React.FC = () => {
  const { t } = useTranslation('settings')
  const { data, loading, error, refetch } = useCardPaymentMethodsQuery({
    variables: {
      page: {
        pageSize: 200,
      },
    },
    fetchPolicy: 'cache-and-network',
  })
  const { router } = useModal()
  const { track } = useSegment()
  const [, { isAuthorized: isStripeEnabled }] = usePermission(STRIPE_ENABLED)
  const { data: entityData } = useEntityQuery()
  const { travelRuleSingaporeRequired, loading: travelRuleLoading } =
    useTravelRuleRequired()

  const addCard = useCallback(() => {
    if (isStripeEnabled) {
      track(SegmentEvents.AddStripeCardClicked)
      router.push({
        pathname:
          routes.account.billing.paymentMethod.addCreditCard.stripeDetails,
      })
    } else {
      track(SegmentEvents.AddCardClicked)
      router.push(routes.account.billing.paymentMethod.addCreditCard.details)
    }
  }, [isStripeEnabled, router, track])

  if (travelRuleSingaporeRequired || travelRuleLoading) {
    return null
  }

  return (
    <div>
      <div
        className="mb-4 text-xl leading-7 text-black-700 font-circular-bold"
        data-testid="linked-cards-table"
      >
        {t('billing.linkedCards.title')}
      </div>
      <Card className="overflow-hidden">
        <SimpleTable
          className="-m-px"
          empty={t`billing.linkedCards.table.emptyLabel`}
          error={error}
          loading={loading}
          numLoadingColumns={3}
          numLoadingRows={2}
          retry={refetch}
          size={data?.cardPaymentMethods.list.length}
          hoverHighlight
        >
          <Table.Body className="m-0">
            {data?.cardPaymentMethods.list.map((card, key) => (
              <LinkedCardsTableRow key={key} card={card} />
            ))}
          </Table.Body>
        </SimpleTable>
      </Card>
      {entityData?.entity?.legalEntity === LegalEntity.CIRCLE_INC && (
        <Buttons.DashboardManagement
          className="mt-2"
          onClick={addCard}
          variant="text"
        >
          {t('settings:billing.add-credit-card')}
        </Buttons.DashboardManagement>
      )}
    </div>
  )
}
